import { AxiosResponse, isAxiosError } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { httpGET, httpPATCH, httpPOST } from '../../services/http.ts';
import { EmailSubscriptionQuery, buildEmailSubscriptionListUrl } from '../../types/emailSubscription.ts';
import { emailSubscriptionReducer } from '../reducers/emailSubscription.reducer.ts';
import { getEmailSubscriptionsQuery } from '../selectors/emailSubscription.selector.ts';

const {
  loadEmailSubscriptions,
  setEmailSubscriptions,
  setEmailSubscriptionsError,
  sendEmail,
  setSendEmailSuccess,
  setSendEmailError,
  activateEmailSubscription,
  setActivateEmailSubscriptionSuccess,
  setActivateEmailSubscriptionError,
} = emailSubscriptionReducer.actions;

function* loadEmailSubscriptionsAction(action: ReturnType<typeof loadEmailSubscriptions>) {
  try {
    const { data }: AxiosResponse = yield call(httpGET, buildEmailSubscriptionListUrl(action.payload));
    yield put(setEmailSubscriptions(data));
  } catch (error) {
    let errorMessage = 'Failed to load email subscriptions.';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setEmailSubscriptionsError(errorMessage));
  }
}

function* sendEmailAction(action: ReturnType<typeof sendEmail>) {
  try {
    yield call(httpPOST, '/email-subscriptions/send', action.payload);
    yield put(setSendEmailSuccess());
  } catch (error) {
    let errorMessage = 'Failed to send emails.';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setSendEmailError(errorMessage));
  }
}

function* activateEmailSubscriptionAction(action: ReturnType<typeof activateEmailSubscription>) {
  try {
    yield call(httpPATCH, `/email-subscriptions/activate/${action.payload}`);
    yield put(setActivateEmailSubscriptionSuccess());
    const query: EmailSubscriptionQuery = yield select(getEmailSubscriptionsQuery);
    yield put(loadEmailSubscriptions(query));
  } catch (error) {
    let errorMessage = 'Failed to activate email subscription.';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setActivateEmailSubscriptionError(errorMessage));
  }
}

export default function* emailSubscriptionSaga() {
  yield all([
    takeLatest(loadEmailSubscriptions, loadEmailSubscriptionsAction),
    takeLatest(sendEmail, sendEmailAction),
    takeLatest(activateEmailSubscription, activateEmailSubscriptionAction),
  ]);
}
